import React, { useState } from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  VStack,
  FormControl,
  FormLabel,
  SimpleGrid } from
'@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import { ArrowLeft, Users } from 'lucide-react';

const FORMINIT_ENDPOINT = 'https://forminit.com/f/p5a5kpiqkte';

interface FormValues {
  name: string;
  email: string;
  telegram: string;
  heardAboutUs: string;
  honeypot: string;
}
export function RequestAccessPage() {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      email: '',
      telegram: '',
      heardAboutUs: '',
      honeypot: ''
    },
    validate: (values) => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      if (!values.name.trim()) errors.name = 'Name is required';
      if (!values.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = 'Invalid email address';
      }
      if (!values.heardAboutUs.trim())
      errors.heardAboutUs = 'This field is required';
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (values.honeypot) {
          setSubmitting(false);
          return;
        }

        setSubmitError(false);

        const formData = new FormData();
        formData.append('fi-sender-firstName', values.name);
        formData.append('fi-sender-email', values.email);
        formData.append('fi-text-heardAboutUs', values.heardAboutUs);
        if (values.telegram) {
          formData.append('fi-text-telegram', values.telegram);
        }

        const response = await fetch(FORMINIT_ENDPOINT, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        });

        if (response.ok) {
          setIsSubmitted(true);
        } else {
          console.error('Submission error:', response.status, await response.text());
          setSubmitError(true);
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setSubmitError(true);
      } finally {
        setSubmitting(false);
      }
    }
  });
  const inputStyles = {
    bg: 'brand.dark',
    border: '1px solid',
    borderColor: 'brand.border',
    borderRadius: 'lg',
    color: 'white',
    fontSize: 'sm',
    px: 4,
    py: 3,
    h: 'auto',
    _placeholder: {
      color: 'gray.500'
    },
    _focus: {
      ring: 2,
      ringColor: 'brand.green',
      borderColor: 'transparent'
    }
  };
  return (
    <Box
      as="section"
      minH="calc(100vh - 4rem)"
      py={{
        base: 12,
        md: 20
      }}>
      
      <Container maxW="container.xl" px={4}>
        <SimpleGrid
          columns={{
            base: 1,
            lg: 2
          }}
          spacing={{
            base: 12,
            lg: 16
          }}
          maxW="5xl"
          mx="auto"
          alignItems="start">

      

          
          {/* Left Column */}
          <Box pt={2}>


                <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="sm"
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={4}
              py={2}
              fontWeight="semibold"
              color="white"
              borderColor="brand.border"
              borderRadius="lg"
              _hover={{
                bg: 'brand.card'
              }}
              mb={10}
              h="auto">
              
              <ArrowLeft size={16} />
              Go Back
            </Button>
         
            {/* <Box
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={3}
              py={1.5}
              borderRadius="full"
              bg="rgba(16, 185, 129, 0.1)"
              border="1px solid"
              borderColor="rgba(16, 185, 129, 0.2)"
              color="brand.green"
              fontSize="sm"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={6}
              >
              
              Limited Access
            </Box> */}

            <Heading
              as="h1"
              fontSize={{
                base: '3xl',
                md: '4xl',
                lg: '5xl'
              }}
              fontWeight="bold"
              letterSpacing="tight"
              mb={6}
              lineHeight="tight">
              
              Get Early Access to{' '}
              <Text as="span" color="brand.green">
                GMCRadar
              </Text>
            </Heading>

            <Text fontSize="lg" color="gray.400" lineHeight="relaxed" mb={8}>
              We're limiting access to ensure quality support for our early
              users. Join the waitlist now to secure your spot.
            </Text>

            <Flex align="center" gap={3}>
              <Flex
                w={10}
                h={10}
                borderRadius="full"
                bg="rgba(16, 185, 129, 0.1)"
                border="1px solid"
                borderColor="rgba(16, 185, 129, 0.2)"
                align="center"
                justify="center">
                
                <Users color="#10b981" size={20} />
              </Flex>
              <Text color="white" fontWeight="semibold">
                Limited Access
              </Text>
            </Flex>
          </Box>

          {/* Right Column - Form */}
          <Box
            bg="brand.card"
            border="1px solid"
            borderColor="brand.border"
            borderRadius="2xl"
            p={{
              base: 6,
              md: 8
            }}>
            
            <Heading
              as="h2"
              fontSize="xl"
              fontWeight="bold"
              color="white"
              mb={6}>
              
              Request Access Now
            </Heading>

            {isSubmitted ? (
              <VStack spacing={4} py={8} textAlign="center">
                <Text fontSize="2xl">🎉</Text>
                <Heading as="h3" fontSize="lg" color="white">
                  You're on the list!
                </Heading>
                <Text color="gray.400" fontSize="sm">
                  We'll be in touch soon.
                </Text>
              </VStack>
            ) : (
            <form onSubmit={formik.handleSubmit}>
              {/* Honeypot — hidden from real users, bots fill it in */}
              <Box
                position="absolute"
                left="-9999px"
                aria-hidden="true"
                tabIndex={-1}>
                <input
                  type="text"
                  name="honeypot"
                  value={formik.values.honeypot}
                  onChange={formik.handleChange}
                  tabIndex={-1}
                  autoComplete="off" />
              </Box>
              <VStack spacing={5}>
                <FormControl>
                  <FormLabel
                    htmlFor="name"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="white"
                    mb={2}>
                    
                    Your Name
                    <Text as="span" color="brand.green">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    {...inputStyles} />
                  
                  {formik.touched.name && formik.errors.name &&
                  <Text color="red.400" fontSize="xs" mt={1}>
                      {formik.errors.name}
                    </Text>
                  }
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="email"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="white"
                    mb={2}>
                    
                    Email Address
                    <Text as="span" color="brand.green">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    {...inputStyles} />
                  
                  {formik.touched.email && formik.errors.email &&
                  <Text color="red.400" fontSize="xs" mt={1}>
                      {formik.errors.email}
                    </Text>
                  }
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="telegram"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="white"
                    mb={2}>
                    
                    What is your Telegram ID?
                  </FormLabel>
                  <Input
                    id="telegram"
                    name="telegram"
                    type="text"
                    placeholder="@yourtelegramid"
                    value={formik.values.telegram}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    {...inputStyles} />
                  
                </FormControl>

                <FormControl>
                  <FormLabel
                    htmlFor="heardAboutUs"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="white"
                    mb={2}>
                    
                    Where did you hear of us?
                    <Text as="span" color="brand.green">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    id="heardAboutUs"
                    name="heardAboutUs"
                    type="text"
                    placeholder="Tell us how you found us"
                    value={formik.values.heardAboutUs}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    {...inputStyles} />
                  
                  {formik.touched.heardAboutUs &&
                  formik.errors.heardAboutUs &&
                  <Text color="red.400" fontSize="xs" mt={1}>
                        {formik.errors.heardAboutUs}
                      </Text>
                  }
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  px={6}
                  py={3.5}
                  h="auto"
                  fontSize="md"
                  fontWeight="bold"
                  color="brand.dark"
                  bg="brand.green"
                  borderRadius="lg"
                  isLoading={formik.isSubmitting}
                  _hover={{
                    bg: 'brand.greenHover',
                    boxShadow: 'glow-strong'
                  }}>

                  <Text as="span">Get Approved Now</Text>
                  <ArrowLeft
                    size={18}
                    style={{
                      transform: 'rotate(180deg)'
                    }} />

                </Button>

                {submitError &&
                  <Text color="red.400" fontSize="sm" textAlign="center">
                    Something went wrong. Please try again.
                  </Text>
                }

                <Text fontSize="xs" color="gray.500" textAlign="center">
                  By submitting, you agree to our{' '}
                  <Text
                    as={RouterLink}
                    to="/policies/terms-of-service"
                    onClick={() => window.scrollTo(0, 0)}
                    color="gray.400"
                    textDecoration="underline"
                    cursor="pointer">

                    Terms of Service
                  </Text>{' '}
                  and{' '}
                  <Text
                    as={RouterLink}
                    to="/policies/privacy-policy"
                    onClick={() => window.scrollTo(0, 0)}
                    color="gray.400"
                    textDecoration="underline"
                    cursor="pointer">

                    Privacy Policy
                  </Text>
                </Text>
              </VStack>
            </form>
            )}
          </Box>
        </SimpleGrid>
      </Container>
    </Box>);

}
